import { GetRef, Input } from 'antd';
import React from 'react';
import { BCNotification, findMessage } from '..';
import { ITextAreaChangeEventArgs, ITextAreaProps } from './entities';

const { TextArea } = Input;
type TextAreaRef = GetRef<typeof TextArea>;

const BCTextArea = React.forwardRef(
	(props: ITextAreaProps, ref: React.ForwardedRef<TextAreaRef>) => {
		const [value, setValue] = React.useState(props.value);
		const textareaRef = React.useRef<TextAreaRef>();
		React.useImperativeHandle(ref, () => textareaRef.current);

		React.useEffect(() => {
			if (textareaRef.current) {
				textareaRef.current.resizableTextArea?.textArea?.addEventListener?.(
					'keydown',
					event => {
						if (event.which == 33) {
							window.scrollTo(0, window.scrollY);
							event.preventDefault();
							(event.target as HTMLInputElement).setSelectionRange(0, 0);
							(event.target as HTMLInputElement).scrollTop = 0;
						}
						if (event.which == 34) {
							window.scrollTo(0, window.scrollY);
							event.preventDefault();
							const content = (textareaRef.current as any).resizableTextArea?.textArea
								?.value;
							if (content?.length) {
								const textareaLength = content.length;
								(event.target as HTMLInputElement).setSelectionRange(
									textareaLength,
									textareaLength
								);
								(event.target as HTMLInputElement).scrollTop = 9999;
							}
						}
					}
				);
			}
		}, []);

		React.useEffect(() => {
			setValue(props.value);
		}, [props.value]);

		const onChange = (e: any) => {
			if (props.maxLength === 0) {
				return;
			}

			let validControl = false;
			if (props.regExp?.regExpString) {
				validControl = validationControl(e.currentTarget.value);
			}

			if (!validControl) {
				const args: ITextAreaChangeEventArgs = {
					senderArgs: e,
					data: e.currentTarget.value,
					itemKey: props.itemKey
				};
				setValue(args.data);
				triggerOnChange(args);
			}
		};

		const triggerOnChange = (args: ITextAreaChangeEventArgs) => {
			if (props.onChange) {
				props.onChange(args);
			}
		};

		const validationControl = (value?: string): boolean => {
			const validFormat = value.match(props.regExp.regExpString);
			if (value && !validFormat) {
				BCNotification.error({
					message: findMessage.get('101291'),
					description: props.regExp.validationFailedMessage || findMessage.get('101313')
				});

				setValue(props.value);
				return true;
			} else {
				if (!value && !props.regExp.allowNull) {
					setValue(value);
					return true;
				}
			}

			return false;
		};

		const style: React.CSSProperties = {
			marginBottom: props.showCount ? '16px' : 'unset'
		};

		const styles: Record<string, React.CSSProperties> = {
			textarea: {
				...props.style,
				textAlign: props.textAlign || 'unset'
			}
		};

		return (
			<TextArea
				tabIndex={props.tabIndex}
				placeholder={props.placeHolder}
				size={props.size}
				style={style}
				styles={styles}
				value={value}
				rows={props.rows}
				onChange={onChange}
				autoSize={props.autosize}
				readOnly={props.readOnly}
				disabled={props.disabled}
				className={props.className}
				maxLength={props.maxLength}
				showCount={props.showCount}
				onBlur={props.onBlur}
				ref={textareaRef}
				data-selenium-id={props.seleniumId}
				onPressEnter={props.onPressEnter}
				variant={props.variant}
			/>
		);
	}
);

export default BCTextArea;

export { TextAreaRef as BCTextAreaRef, ITextAreaChangeEventArgs, ITextAreaProps };
